import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

import { getCompanyLogo } from '../../../../../../../lib/api/company';
import CustomButton from '../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../lib/designSystem/CircularProgress';
import { showAlert } from '../../../../../../../lib/functions/utils';
import { AuthState } from '../../../../../../../services/redux/authSlice';
import { generateConfirmingSimulationPDF } from '../../../common/exportSimulationConfirming';

interface ExportDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedRequest: any;
}

const ExportDialog = (props: ExportDialogProps) => {
  const { open, setOpen, selectedRequest } = props;
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  // Checkboxes para controlar qué elementos mostrar en el PDF
  const [showClientInfo, setShowClientInfo] = useState(true);
  const [showRequestInfo, setShowRequestInfo] = useState(true);
  const [showDates, setShowDates] = useState(true);

  // Checkboxes específicos para tasas y parámetros
  const [showAdvance, setShowAdvance] = useState(true);
  const [showRate, setShowRate] = useState(true);
  const [showArrearRate, setShowArrearRate] = useState(true);

  const [showAmounts, setShowAmounts] = useState(true);
  const [showDocuments, setShowDocuments] = useState(true);
  const [showOperationCosts, setShowOperationCosts] = useState(true);
  const [showExpenses, setShowExpenses] = useState(true);
  const [showAccountReceivable, setShowAccountReceivable] = useState(true);

  const buildSelectedRequest = () => {
    return {
      ...selectedRequest,
      showClientInfo,
      showRequestInfo,
      showDates,
      showAdvance,
      showRate,
      showArrearRate,
      showAmounts,
      showDocuments,
      showOperationCosts,
      showExpenses,
      showAccountReceivable,
    };
  };

  const handleExportSimulation = async () => {
    setLoading(true);
    try {
      const imageBase64 = await getCompanyLogo(dispatch);
      const requestWithConfig = buildSelectedRequest();
      await generateConfirmingSimulationPDF(
        requestWithConfig,
        user?.name ?? '',
        imageBase64,
      );
      showAlert(dispatch, 'success', 'Simulación exportada correctamente');
    } catch (error) {
      console.log(error);
      showAlert(dispatch, 'error', 'Error al exportar simulación');
    }
    setLoading(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        Exportar simulación solicitud <strong>#{selectedRequest.id}</strong>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <div style={{ minHeight: 200, minWidth: 500 }}>
            <CenteredCircularProgress type="dialog" />
          </div>
        )}
        {!loading && (
          <div>
            <Typography>
              Selecciona qué elementos incluir en el documento PDF.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}
                >
                  Información del Cliente
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showClientInfo}
                      onChange={(e) => setShowClientInfo(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Información del cliente"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showRequestInfo}
                      onChange={(e) => setShowRequestInfo(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Información de la solicitud"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showDates}
                      onChange={(e) => setShowDates(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Fechas de operación"
                />
              </Grid>
              <Grid item xs={4}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}
                >
                  Tasas y Parámetros
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showAdvance}
                      onChange={(e) => setShowAdvance(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Anticipo"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showRate}
                      onChange={(e) => setShowRate(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Diferencia de precio"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showArrearRate}
                      onChange={(e) => setShowArrearRate(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Tasa de mora"
                />
              </Grid>
              <Grid item xs={4}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}
                >
                  Detalles de la Operación
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showAmounts}
                      onChange={(e) => setShowAmounts(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Montos principales"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showDocuments}
                      onChange={(e) => setShowDocuments(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Tabla de documentos"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showOperationCosts}
                      onChange={(e) => setShowOperationCosts(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Costos de operación"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showExpenses}
                      onChange={(e) => setShowExpenses(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Gastos adicionales"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showAccountReceivable}
                      onChange={(e) =>
                        setShowAccountReceivable(e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Cuenta por cobrar"
                />
              </Grid>
            </Grid>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          Cancelar
        </CustomButton>
        <CustomButton color="primary" onClick={() => handleExportSimulation()}>
          Exportar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;
